import { Profile } from "@WahooFitness/cloud-client-types";
import { WfIconNavigationChevronLeftRegular } from "@WahooFitness/redesignr";
import { t } from "@lingui/macro";
import {
  ErrorBoundary,
  GlobalHeader,
  hasMinProfile,
  postRoute,
  useUserContext,
} from "@WahooFitness/wahoo-offline-mfe";
import { Box, Container } from "@mui/material";
import React, { useCallback, useMemo } from "react";
import SettingsFooter from "./SettingsFooter";
import { AccountSection, AthleteProfileSection, HelpSection, SettingsSection } from "./Sections";
import useSettings from "./useSettings";
import useFitnessProfiles from "@/hooks/useFitnessProfiles";
import useFitnessProfileSettings from "@/hooks/useFitnessProfileSettings";

const Settings: React.FC = () => {
  const { currentUser, userError, mutateUser } = useUserContext();

  const { setupLink, subscriptionStatus, logSettingsMenuEvent } = useSettings();

  const goHome = useCallback(() => postRoute("home"), []);

  const showSubmitFeedback = useMemo(
    () => hasMinProfile(Profile.beta, currentUser?.app?.profile),
    [currentUser?.app?.profile]
  );

  const { showMissingFitnessProfileBadge, cyclingFitnessProfile, runningFitnessProfile } =
    useFitnessProfiles();

  const {
    selectedFitnessProfiles,
    error: fitnessProfileSettingsError,
    isLoading: fitnessProfileSettingsLoading,
  } = useFitnessProfileSettings();

  return (
    <>
      <GlobalHeader
        title={t`Account`}
        backAction={goHome}
        primaryLeftActionItem={{
          icon: <WfIconNavigationChevronLeftRegular />,
          action: goHome,
        }}
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary
          error={userError || fitnessProfileSettingsError}
          isLoading={fitnessProfileSettingsLoading}
          onReload={mutateUser}
        >
          <Box display="flex" flexDirection="column" pb={2}>
            <AccountSection
              currentUser={currentUser}
              subscriptionStatus={subscriptionStatus}
              logSettingsMenuEvent={logSettingsMenuEvent}
            />
            <AthleteProfileSection
              showMissingFitnessProfileBadge={showMissingFitnessProfileBadge}
              cyclingFitnessProfile={cyclingFitnessProfile}
              runningFitnessProfile={runningFitnessProfile}
              selectedFitnessProfiles={selectedFitnessProfiles}
            />
            <SettingsSection />
            <HelpSection setupLink={setupLink} showSubmitFeedback={showSubmitFeedback} />
            <SettingsFooter />
          </Box>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default Settings;
